<header class="border-weak bg-surface flex h-10 shrink-0 items-center gap-1 border-b px-3">
  <span
    class="bg-accent-interactive text-accent-main mr-1 flex size-6 items-center justify-center rounded-sm">
    <i [class]="ICONS.sparkle"></i>
  </span>
  <h2 class="text-text-primary truncate text-sm font-semibold">
    {{ 'assistant.title' | translate }}
  </h2>
  @if (assistant.level(); as level) {
    <span
      class="bg-accent-interactive text-accent-main text-2xs inline-flex h-4.5 cursor-help items-center gap-1 rounded-full px-1.5 font-semibold"
      [title]="'assistant.level_hint.' + level | translate">
      <span class="size-1.5 rounded-full bg-current"></span>
      {{ 'assistant.level.' + level | translate }}
    </span>
  }
  <span class="flex-1"></span>
  <button
    type="button"
    class="btn btn-icon"
    [attr.aria-label]="'assistant.new_conversation' | translate"
    [title]="'assistant.new_conversation' | translate"
    (click)="assistant.newTab()">
    <i [class]="ICONS.plus"></i>
  </button>
  <button
    type="button"
    class="btn btn-icon"
    [attr.aria-label]="'assistant.settings.title' | translate"
    [title]="'assistant.settings.title' | translate"
    [attr.aria-pressed]="showSettings()"
    (click)="showSettings.set(!showSettings())">
    <i [class]="ICONS.gear"></i>
  </button>
  @if (assistant.presentation() === 'side') {
    <button
      type="button"
      data-testid="assistant-size-toggle"
      class="btn btn-icon hidden lg:inline-flex"
      [attr.aria-label]="sizeToggleLabel()"
      [title]="sizeToggleLabel()"
      [attr.aria-pressed]="assistant.panelSize() === 'large'"
      (click)="assistant.togglePanelSize()">
      <i
        [class]="
          assistant.panelSize() === 'large' ? ICONS.arrowsInSimple : ICONS.arrowsOutSimple
        "></i>
    </button>
  }
  @if (assistant.presentation() !== 'window') {
    <button
      type="button"
      data-testid="assistant-focus-toggle"
      class="btn btn-icon hidden lg:inline-flex"
      [attr.aria-label]="focusLabel()"
      [title]="focusLabel()"
      [attr.aria-pressed]="assistant.presentation() === 'focus'"
      (click)="assistant.toggleFocus()">
      <i
        [class]="
          assistant.presentation() === 'focus' ? ICONS.cornersIn : ICONS.cornersOut
        "></i>
    </button>
    @if (host.available) {
      <button
        type="button"
        data-testid="assistant-pop-out"
        class="btn btn-icon hidden lg:inline-flex"
        [attr.aria-label]="popOutLabel()"
        [title]="popOutLabel()"
        [disabled]="assistant.anyStreaming()"
        (click)="host.popOut()">
        <i [class]="ICONS.arrowSquareOut"></i>
      </button>
    }
    <button
      type="button"
      data-testid="assistant-close"
      class="btn btn-icon"
      [attr.aria-label]="'assistant.close' | translate"
      [title]="'assistant.close' | translate"
      (click)="close()">
      <i [class]="ICONS.x"></i>
    </button>
  }
</header>
@if (host.blocked()) {
  <p
    role="status"
    data-testid="assistant-popup-blocked"
    class="text-text-secondary border-weak border-b px-3 py-2 text-xs">
    {{ 'assistant.popup_blocked' | translate }}
  </p>
}
<app-assistant-tabs />

@if (assistant.configError(); as code) {
  <p class="text-text-secondary p-4 text-sm">
    {{
      (code === 'internal' ? 'assistant.config_error' : 'assistant.errors.' + code)
        | translate
    }}
  </p>
} @else if (assistant.config() && !assistant.access().use) {
  <p class="text-text-secondary p-4 text-sm">{{ 'assistant.unavailable' | translate }}</p>
} @else if (assistant.notConfigured(); as who) {
  <p class="text-text-secondary p-4 text-sm">
    @if (who === 'admin') {
      {{ 'assistant.not_configured_admin' | translate }}
      <a class="text-accent-main hover:text-accent-hover underline" [href]="settingsLink()">
        {{ 'assistant.configure' | translate }}
      </a>
    } @else {
      {{ 'assistant.not_configured_user' | translate }}
    }
  </p>
} @else if (assistant.config()) {
  @if (assistant.homeDataSpaceId() !== null) {
    <p class="text-text-tertiary px-3 pt-2 text-xs">
      {{ 'assistant.home_configuration' | translate }}
    </p>
  }
  @if (showSettings() || !assistant.selectedConnection() || assistant.needsKey()) {
    <app-assistant-settings
      [connections]="assistant.connections()"
      [connectionId]="assistant.selectedConnection()?.id ?? null"
      [model]="assistant.model()"
      [apiKey]="assistant.apiKey()"
      (connectionChange)="assistant.selectConnection($event)"
      (modelChange)="assistant.selectModel($event)"
      (apiKeyChange)="assistant.setApiKey($event)" />
  }

  <div class="relative flex min-h-0 flex-1 flex-col">
    <!-- isolate keeps the tables' sticky z-indices inside the transcript, under the jump-to-latest pill. -->
    <div
      #scroller
      role="log"
      [attr.aria-busy]="assistant.streaming()"
      class="isolate flex min-h-0 flex-1 flex-col overflow-y-auto p-3"
      [class.assistant-wide]="wide()"
      (scroll)="onScroll(scroller)">
      <div class="flex flex-col gap-3" [class]="column()">
        @if (assistant.contextNotice(); as notice) {
          <p
            role="note"
            data-testid="context-notice"
            class="text-text-secondary flex items-start gap-1.5 text-xs">
            <i [class]="ICONS.info" class="mt-0.5 shrink-0"></i>
            <span>{{ 'assistant.context_notice.' + notice | translate }}</span>
          </p>
        }
        @for (message of assistant.messages(); track message.id; let i = $index) {
          <app-assistant-message
            [message]="message"
            [showContext]="captioned()[i]"
            [last]="$last" />
        } @empty {
          <p class="text-text-tertiary text-sm">{{ 'assistant.empty' | translate }}</p>
        }
      </div>
    </div>
    @if (!following() && assistant.messages().length > 0) {
      <button
        type="button"
        data-testid="jump-to-latest"
        class="btn btn-secondary btn-sm bg-elevated absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full shadow-md"
        (click)="jumpToLatest()">
        <i [class]="ICONS.arrowDown"></i>{{ 'assistant.chrome.jump_to_latest' | translate }}
      </button>
    }
  </div>

  @if (assistant.needsKey()) {
    <div class="border-weak border-t px-3 pt-2">
      <p class="form-help mt-0" [class]="column()">
        {{ 'assistant.settings.api_key_required' | translate }}
      </p>
    </div>
  }
  <div class="border-weak border-t p-3">
    <form class="flex flex-col gap-1.5" [class]="column()" (submit)="submit($event)">
      <app-assistant-context-line />
      <!-- One box for the text and its controls; the box, not the textarea, shows keyboard focus. -->
      <div
        class="bg-surface border-default focus-within:border-accent-focus focus-within:ring-accent-focus/25 flex flex-col rounded-lg border transition-colors focus-within:ring-3">
        <textarea
          #input
          class="text-text-primary placeholder:text-text-tertiary max-h-[min(40vh,18rem)] min-h-9 w-full resize-none overflow-y-auto bg-transparent px-3 pt-2.5 pb-1 text-sm outline-none"
          rows="2"
          [attr.aria-label]="'assistant.input_placeholder' | translate"
          [placeholder]="
            (assistant.hasPendingApprovals()
              ? 'assistant.input_placeholder_pending'
              : 'assistant.input_placeholder'
            ) | translate
          "
          [value]="assistant.draft()"
          (input)="assistant.draft.set($any($event.target).value)"
          (keydown)="onKeydown($event)"></textarea>
        <div class="flex items-center gap-0.5 px-1.5 pb-1.5">
          <div
            class="[&_.gx-select]:text-text-secondary [&_.gx-select]:hover:bg-interactive min-w-0 [&_.gx-select]:w-auto [&_.gx-select]:gap-1 [&_.gx-select]:rounded-sm [&_.gx-select]:border-transparent [&_.gx-select]:bg-transparent [&_.gx-select]:px-1.5 [&_.gx-select]:py-0.5 [&_.gx-select]:text-xs"
            [title]="'assistant.composer.model_hint' | translate">
            <label for="assistant-model" class="sr-only">{{
              'assistant.composer.model' | translate
            }}</label>
            <app-select
              [id]="'assistant-model'"
              data-testid="model-chip"
              [items]="modelItems()"
              [value]="assistant.model() ?? undefined"
              (valueChange)="assistant.selectModel($any($event))" />
          </div>
          @if (assistant.level() === 'write' || assistant.level() === 'plan') {
            <div
              class="[&_.gx-select]:text-text-secondary [&_.gx-select]:hover:bg-interactive min-w-0 [&_.gx-select]:w-auto [&_.gx-select]:gap-1 [&_.gx-select]:rounded-sm [&_.gx-select]:border-transparent [&_.gx-select]:bg-transparent [&_.gx-select]:px-1.5 [&_.gx-select]:py-0.5 [&_.gx-select]:text-xs">
              <label for="assistant-approval-mode" class="sr-only">{{
                'assistant.approval_mode.label' | translate
              }}</label>
              <app-select
                [id]="'assistant-approval-mode'"
                data-testid="approval-mode"
                [items]="modeItems()"
                [value]="assistant.approvalMode()"
                [leadIcon]="
                  assistant.approvalMode() === 'auto_all'
                    ? ICONS.shieldWarning
                    : ICONS.shield
                "
                (valueChange)="assistant.setApprovalMode($any($event))" />
            </div>
          }
          <span class="flex-1"></span>
          @if (assistant.streaming()) {
            <button
              type="button"
              class="btn btn-secondary btn-icon"
              [attr.aria-label]="'assistant.stop' | translate"
              [title]="'assistant.stop' | translate"
              (click)="assistant.stop()">
              <i [class]="ICONS.stopCircle"></i>
            </button>
          } @else {
            <button
              type="submit"
              class="btn btn-primary btn-icon"
              [disabled]="
                !assistant.draft().trim() ||
                assistant.needsKey() ||
                assistant.busyElsewhere()
              "
              [attr.aria-label]="
                (assistant.busyElsewhere() ? 'assistant.busy.send' : 'assistant.send')
                  | translate
              "
              [title]="
                (assistant.busyElsewhere() ? 'assistant.busy.send' : 'assistant.send')
                  | translate
              ">
              <i [class]="ICONS.paperPlaneTilt"></i>
            </button>
          }
        </div>
      </div>
      @if (
        assistant.approvalMode() === 'auto_all' &&
        (assistant.level() === 'write' || assistant.level() === 'plan')
      ) {
        <p class="text-text-secondary text-2xs flex items-start gap-1.5">
          <i [class]="ICONS.warning" class="text-danger"></i>
          {{ 'assistant.approval_mode.auto_all_warning' | translate }}
        </p>
      }
    </form>
  </div>
} @else {
  <p class="text-text-tertiary p-4 text-sm">{{ 'assistant.loading' | translate }}</p>
}
